import React, { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Order, { SanitizedOrder } from '../../../../types/order/Order';
import { useSubmittedDesignCookie } from '../../../../hooks/useSubmittedDesignCookie';

// Contexts
import OrderDetailContext from '../contexts/OrderDetailContext';

import OrderData from './OrderData';

interface OrderWrapperProps {
  partialOrderData: Order;
  fullOrderData?: SanitizedOrder;
}

const OrderWrapper = ({ partialOrderData, fullOrderData }: OrderWrapperProps) => {
  useSubmittedDesignCookie();
  const queryClient = useQueryClient();

  // ReFetch the order whenever needed
  const reFetchOrder = useCallback(async ({
    reFetchPaymentLink = false,
  }) => {
    if (!fullOrderData) {
      return;
    }

    // ReFetch the order data itself
    await queryClient.refetchQueries({
      queryKey: ['sanitized-order', fullOrderData.id],
    });

    if (reFetchPaymentLink) {
      // ReFetch the paymentLink
      await queryClient.refetchQueries({
        queryKey: ['order', 'get-payment-link', fullOrderData.id],
      });
    }
  }, [fullOrderData?.id, fullOrderData?.version]);

  const OrderDetailContent = useMemo(() => ({
    order: fullOrderData,
    reFetchOrder,
  }), [fullOrderData?.id, fullOrderData?.version, reFetchOrder]);

  return (
    <OrderDetailContext.Provider value={OrderDetailContent}>
      <OrderData partialOrderData={partialOrderData} fullOrderData={fullOrderData} />
    </OrderDetailContext.Provider>
  );
};

export default OrderWrapper;
